import React, { useEffect, useState } from 'react';
import {
  X, CheckCheck, AlertTriangle, AlertCircle, Trash2,
  CheckCircle2, XCircle, ChevronDown, ChevronRight, Activity,
  ShieldCheck, Bot, User, ArrowUpRight, Clock, CheckSquare, XSquare, Settings, KeyRound, MessagesSquare,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useAlertStore, AlertNotification } from '../store/alertStore';
import { useRunLogStore, RunLog, NodeAudit } from '../store/runLogStore';
import { useHumanActionsStore, ActionExecution } from '../store/humanActionsStore';
import { useApprovalStore, ApprovalRequest } from '../store/approvalStore';
import { useNavigationStore } from '../store/navigationStore';
import { openAcademy } from '../modules/academy/academyRoute';
import { communityQuestionOf } from './communityNotice';

interface Props {
  onClose: () => void;
}

// ─── Approvals tab ────────────────────────────────────────────────────────────

// A purpose activation request (INTRO-11): who asks, for which purpose, why
// and for how long. The drawer is always dark, so the block pins the dark
// tokens with data-theme instead of hard-coding colors.
const PurposeRequestSummary: React.FC<{ req: ApprovalRequest }> = ({ req }) => {
  const { t } = useTranslation();
  const p = (req.payload || {}) as { purpose_name?: string; justification?: string; duration_minutes?: number; markings?: string[] };
  const mins = Number(p.duration_minutes || 0);
  const duration = mins % 60 === 0 ? `${mins / 60} h` : `${mins} min`;
  return (
    <div data-theme="dark" data-testid={`purpose-request-${req.id}`} style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8, padding: '8px 10px', borderRadius: 5, backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: 'var(--color-text)' }}>
        <KeyRound size={12} style={{ color: 'var(--color-warn)' }} />
        {t('shell.purposeRequestTitle', { name: p.purpose_name || req.resource_id, defaultValue: `Purpose: ${p.purpose_name || req.resource_id}` })}
      </div>
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
        {t('shell.purposeRequestWho', { who: req.requested_by_email || req.requested_by, duration, defaultValue: `${req.requested_by_email || req.requested_by} asks for ${duration}` })}
      </div>
      {p.justification && (
        <div style={{ fontSize: 11.5, color: 'var(--color-text)', lineHeight: 1.45 }}>«{p.justification}»</div>
      )}
    </div>
  );
};

const ApprovalRow: React.FC<{
  req: ApprovalRequest;
  onApprove: () => Promise<void> | void;
  onReject: (reason: string) => Promise<void> | void;
}> = ({ req, onApprove, onReject: onRejectReason }) => {
  const { t } = useTranslation();
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const isPurpose = req.resource_type === 'purpose_activation';
  const run = async (fn: () => Promise<void> | void) => {
    setError(null);
    try { await fn(); } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };
  const onReject = () => run(() => onRejectReason(reason.trim()));

  const approvedCount = req.approvals.length;
  const progress = `${approvedCount}/${req.required_approvers || 1}`;
  const isExpired = new Date(req.expires_at) < new Date();

  return (
    <div style={{
      padding: '10px 16px',
      borderBottom: '1px solid #131C2E',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
        <ShieldCheck size={12} style={{ color: '#A78BFA', flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#E2E8F0', marginBottom: 2 }}>
            {isPurpose ? t('shell.purposeRequestKind', 'Purpose activation') : `${req.resource_type} · ${req.operation}`}
          </div>
          <div style={{ fontSize: 11, color: '#64748B', marginBottom: 2 }}>
            {t('shell.requestedBy', 'Requested by')} <span style={{ color: '#94A3B8' }}>{req.requested_by_email || req.requested_by}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, color: '#475569' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Clock size={9} /> {formatTimeAgo(req.created_at, t)}
            </span>
            <span style={{
              padding: '1px 6px', borderRadius: 10,
              backgroundColor: '#312E81', color: '#A5B4FC', fontSize: 10,
            }}>
              {t('shell.approvalsCount', { count: progress, defaultValue: `${progress} approvals` })}
            </span>
            {isExpired && (
              <span style={{ color: '#EF4444', fontSize: 10 }}>{t('shell.expired', 'Expired')}</span>
            )}
          </div>
        </div>
      </div>

      {isPurpose && <PurposeRequestSummary req={req} />}
      {error && (
        <div data-theme="dark" data-testid="approval-error" style={{ marginBottom: 6, fontSize: 11, color: 'var(--color-status-red)' }}>{error}</div>
      )}

      {!isPurpose && req.context && Object.keys(req.context).length > 0 && (
        <div style={{
          marginBottom: 6, padding: '4px 8px',
          backgroundColor: '#0A1220', borderRadius: 4,
          fontSize: 10, color: '#64748B', fontFamily: 'var(--font-mono)',
          maxHeight: 60, overflow: 'hidden',
        }}>
          {JSON.stringify(req.context).slice(0, 120)}
        </div>
      )}

      {!rejectMode ? (
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            data-testid={`approve-${req.id}`}
            onClick={() => run(onApprove)}
            style={{
              flex: 1, height: 24, fontSize: 11, fontWeight: 600,
              cursor: 'pointer', borderRadius: 3, border: 'none',
              backgroundColor: '#065F46', color: '#6EE7B7',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            }}
          >
            <CheckSquare size={11} /> {t('common.approve', 'Approve')}
          </button>
          <button
            data-testid={`reject-${req.id}`}
            onClick={() => setRejectMode(true)}
            style={{
              flex: 1, height: 24, fontSize: 11, fontWeight: 600,
              cursor: 'pointer', borderRadius: 3,
              border: '1px solid #7F1D1D', backgroundColor: 'transparent', color: '#FCA5A5',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            }}
          >
            <XSquare size={11} /> {t('common.reject', 'Reject')}
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <input
            autoFocus
            placeholder={t('shell.reasonForRejection', 'Reason for rejection...')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={{
              height: 28, padding: '0 8px', fontSize: 11,
              backgroundColor: '#0A1220', border: '1px solid #1E2D42',
              borderRadius: 3, color: '#E2E8F0', outline: 'none',
            }}
          />
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              onClick={() => { if (reason.trim()) onReject(); }}
              disabled={!reason.trim()}
              style={{
                flex: 1, height: 22, fontSize: 10, fontWeight: 600,
                cursor: reason.trim() ? 'pointer' : 'not-allowed', borderRadius: 3,
                border: 'none', backgroundColor: reason.trim() ? '#7F1D1D' : '#1E2D42',
                color: '#FCA5A5',
              }}
            >
              {t('shell.confirmReject', 'Confirm Reject')}
            </button>
            <button
              onClick={() => setRejectMode(false)}
              style={{
                width: 64, height: 22, fontSize: 10, fontWeight: 500,
                cursor: 'pointer', borderRadius: 3,
                border: '1px solid #1E2D42', backgroundColor: 'transparent', color: '#64748B',
              }}
            >
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const formatTimeAgo = (iso: string, t: TFunction) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return t('common.justNow', 'just now');
  if (m < 60) return t('common.minutesAgo', { count: m, defaultValue: `${m}m ago` });
  const h = Math.floor(m / 60);
  if (h < 24) return t('common.hoursAgo', { count: h, defaultValue: `${h}h ago` });
  const d = Math.floor(h / 24);
  return t('common.daysAgo', { count: d, defaultValue: `${d}d ago` });
};

// ─── Alert tab ───────────────────────────────────────────────────────────────

const SeverityIcon: React.FC<{ severity: AlertNotification['severity'] }> = ({ severity }) =>
  severity === 'critical'
    ? <AlertCircle size={13} style={{ color: '#EF4444', flexShrink: 0 }} />
    : <AlertTriangle size={13} style={{ color: '#F59E0B', flexShrink: 0 }} />;

const AlertRow: React.FC<{
  notification: AlertNotification;
  onRead: () => void;
  onDelete: () => void;
}> = ({ notification: n, onRead, onDelete }) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = React.useState(false);
  return (
    <div
      onClick={onRead}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '10px 16px',
        borderBottom: '1px solid #131C2E',
        backgroundColor: n.read ? 'transparent' : '#111926',
        cursor: n.read ? 'default' : 'pointer',
        display: 'flex', gap: 10, alignItems: 'flex-start',
      }}
    >
      <SeverityIcon severity={n.severity} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <span style={{
            fontSize: 11, fontWeight: 600,
            color: n.severity === 'critical' ? '#FCA5A5' : '#FCD34D',
            textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            {n.rule_name}
          </span>
          {!n.read && (
            <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#EF4444', flexShrink: 0 }} />
          )}
        </div>
        <div style={{ fontSize: 12, color: '#CBD5E1', lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {n.message}
        </div>
        <div style={{ fontSize: 10, color: '#475569', marginTop: 4, fontFamily: 'var(--font-mono)' }}>
          {formatTimeAgo(n.fired_at, t)}
        </div>
      </div>
      {hovered && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: '#475569', padding: 2, display: 'flex', alignItems: 'center', flexShrink: 0 }}
          title={t('common.dismiss', 'Dismiss')}
        >
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
};

/** An answer in the Academy community (GSL-6): opens the question. The
 *  drawer is always dark, so it uses the always-dark nav tokens. */
const CommunityRow: React.FC<{
  notification: AlertNotification;
  onOpen: () => void;
  onDelete: () => void;
}> = ({ notification: n, onOpen, onDelete }) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = React.useState(false);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-shell="community-notice"
      style={{
        padding: '10px 16px',
        borderBottom: '1px solid var(--color-nav-border)',
        backgroundColor: n.read ? 'transparent' : 'var(--color-nav-hover-bg)',
        cursor: 'pointer',
        display: 'flex', gap: 10, alignItems: 'flex-start',
      }}
    >
      <MessagesSquare size={13} style={{ color: 'var(--color-nav-ai-text)', flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <span style={{
            fontSize: 11, fontWeight: 600, color: 'var(--color-nav-ai-text)',
            textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            {n.rule_name}
          </span>
          {!n.read && (
            <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'var(--color-nav-danger-text)', flexShrink: 0 }} />
          )}
        </div>
        <div style={{ fontSize: 12, color: 'var(--color-nav-text-hover)', lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {n.message}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, color: 'var(--color-nav-icon)', marginTop: 4 }}>
          <span style={{ fontFamily: 'var(--font-mono)' }}>{formatTimeAgo(n.fired_at, t)}</span>
          <span style={{ color: 'var(--color-nav-ai-text)', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 2 }}>
            {t('shell.openCommunityQuestion', 'Open the question')} <ArrowUpRight size={10} />
          </span>
        </div>
      </div>
      {hovered && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-nav-icon)', padding: 2, display: 'flex', alignItems: 'center', flexShrink: 0 }}
          title={t('common.dismiss', 'Dismiss')}
        >
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
};

// ─── Logs tab ─────────────────────────────────────────────────────────────────

const NodeRow: React.FC<{ audit: NodeAudit }> = ({ audit: a }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const stats = a.stats as Record<string, unknown> | undefined;
  const isSource = a.node_type === 'SOURCE';
  const hasDetail = isSource && stats;

  return (
    <div style={{ padding: '4px 0' }}>
      {/* Summary line */}
      <div
        onClick={() => hasDetail && setExpanded((v) => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          fontSize: 11, color: '#94A3B8',
          cursor: hasDetail ? 'pointer' : 'default',
        }}
      >
        {a.status === 'ok'
          ? <CheckCircle2 size={11} style={{ color: '#22C55E', flexShrink: 0 }} />
          : a.status === 'error'
          ? <XCircle size={11} style={{ color: '#EF4444', flexShrink: 0 }} />
          : <Activity size={11} style={{ color: '#64748B', flexShrink: 0 }} />}
        <span style={{ flex: 1 }}>
          <span style={{ color: '#CBD5E1', fontWeight: 500 }}>{a.label || a.node_type}</span>
          {' '}
          <span style={{ fontFamily: 'var(--font-mono)', color: '#64748B' }}>
            {a.rows_in}→{a.rows_out}
          </span>
          {a.duration_ms !== undefined && (
            <span style={{ color: '#475569', marginLeft: 6 }}>{a.duration_ms}ms</span>
          )}
          {isSource && typeof stats?.raw_row_count === 'number' && stats.raw_row_count !== a.rows_out && (
            <span style={{ color: '#F59E0B', marginLeft: 6 }}>({stats.raw_row_count} {t('shell.fromApi', 'from API')})</span>
          )}
        </span>
        {a.error && (
          <span style={{ color: '#FCA5A5', fontSize: 10, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.error}>
            {a.error}
          </span>
        )}
        {hasDetail && (
          expanded
            ? <ChevronDown size={11} style={{ color: '#475569', flexShrink: 0 }} />
            : <ChevronRight size={11} style={{ color: '#475569', flexShrink: 0 }} />
        )}
      </div>

      {/* SOURCE detail */}
      {expanded && hasDetail && (() => {
        const srcUrl = String(stats.url ?? '');
        const configuredEndpoint = String(stats.configured_endpoint ?? stats.endpoint ?? '');
        const httpStatus = stats.http_status != null ? Number(stats.http_status) : null;
        const rawCount = typeof stats.raw_row_count === 'number' ? stats.raw_row_count : null;
        const resolvedParams = stats.resolved_params as Record<string, string> | null | undefined;
        const responseError = stats.response_error ? String(stats.response_error) : null;
        const sourceError = stats.error ? String(stats.error) : null;
        return (
        <div style={{
          marginTop: 6, marginLeft: 19, padding: '8px 10px',
          backgroundColor: '#0A1220', borderRadius: 4, border: '1px solid #1E2D42',
          display: 'flex', flexDirection: 'column', gap: 6,
        }}>
          {/* Source fetch error */}
          {sourceError && (
            <div style={{ fontSize: 11, color: '#FCA5A5', fontWeight: 500, wordBreak: 'break-word' }}>
              {sourceError}
            </div>
          )}

          {/* URL (live) or configured endpoint fallback */}
          {(srcUrl || configuredEndpoint) && (
            <div>
              <div style={{ fontSize: 9, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>
                {srcUrl ? 'Endpoint (resolved)' : 'Endpoint (configured)'}
              </div>
              <div style={{ fontSize: 10, color: '#93C5FD', fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>
                {srcUrl || configuredEndpoint}
              </div>
            </div>
          )}

          {/* HTTP status */}
          {httpStatus !== null && (
            <div style={{ display: 'flex', gap: 16, fontSize: 10 }}>
              <span>
                <span style={{ color: '#475569' }}>HTTP </span>
                <span style={{
                  fontFamily: 'var(--font-mono)', fontWeight: 600,
                  color: httpStatus < 300 ? '#22C55E' : '#EF4444',
                }}>
                  {httpStatus}
                </span>
              </span>
              {rawCount !== null && (
                <span>
                  <span style={{ color: '#475569' }}>{t('shell.rowsReturned', 'rows returned')} </span>
                  <span style={{ fontFamily: 'var(--font-mono)', color: '#E2E8F0' }}>{rawCount}</span>
                </span>
              )}
            </div>
          )}

          {/* Resolved params */}
          {resolvedParams && Object.keys(resolvedParams).length > 0 && (
            <div>
              <div style={{ fontSize: 9, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>
                Query Params
              </div>
              {Object.entries(resolvedParams).map(([k, v]) => (
                <div key={k} style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#94A3B8' }}>
                  <span style={{ color: '#64748B' }}>{k}</span>=<span style={{ color: '#FCD34D' }}>{v}</span>
                </div>
              ))}
            </div>
          )}

          {/* Response / HTTP error */}
          {responseError && (
            <div>
              <div style={{ fontSize: 9, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>Response Error</div>
              <div style={{ fontSize: 10, color: '#FCA5A5', wordBreak: 'break-word', fontFamily: 'var(--font-mono)' }}>
                {responseError}
              </div>
            </div>
          )}

          {/* Sample output rows */}
          {a.sample_out && a.sample_out.length > 0 && (
            <div>
              <div style={{ fontSize: 9, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>
                {t('shell.sampleRows', { count: a.sample_out.length, total: rawCount ?? a.rows_out, defaultValue: `Sample (${a.sample_out.length} of ${rawCount ?? a.rows_out})` })}
              </div>
              <pre style={{
                fontSize: 9, color: '#94A3B8', margin: 0,
                overflow: 'auto', maxHeight: 160,
                backgroundColor: '#060D18', padding: 6, borderRadius: 3,
                whiteSpace: 'pre-wrap', wordBreak: 'break-all',
              }}>
                {JSON.stringify(a.sample_out[0], null, 2)}
              </pre>
            </div>
          )}
        </div>
        );
      })()}
    </div>
  );
};

const LogRow: React.FC<{
  log: RunLog;
  onRead: () => void;
  onDelete: () => void;
}> = ({ log, onRead, onDelete }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = React.useState(false);
  const failed = log.status === 'FAILED';

  return (
    <div
      style={{
        borderBottom: '1px solid #131C2E',
        backgroundColor: log.read ? 'transparent' : '#111926',
      }}
    >
      <div
        onClick={() => { onRead(); setExpanded((v) => !v); }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          padding: '10px 16px', cursor: 'pointer',
          display: 'flex', gap: 10, alignItems: 'flex-start',
        }}
      >
        {failed
          ? <XCircle size={13} style={{ color: '#EF4444', flexShrink: 0, marginTop: 1 }} />
          : <CheckCircle2 size={13} style={{ color: '#22C55E', flexShrink: 0, marginTop: 1 }} />}

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
            <span style={{
              fontSize: 11, fontWeight: 600,
              color: failed ? '#FCA5A5' : '#86EFAC',
              textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              {failed ? t('pipelines.failed', 'Failed') : t('pipelines.completed', 'Completed')}
            </span>
            {!log.read && (
              <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: failed ? '#EF4444' : '#22C55E', flexShrink: 0 }} />
            )}
          </div>
          <div style={{ fontSize: 12, color: '#CBD5E1', lineHeight: 1.4, marginBottom: 2 }}>
            {log.pipeline_name}
          </div>
          {log.error && (
            <div style={{
              fontSize: 11, color: '#FCA5A5', lineHeight: 1.4,
              marginBottom: 3, wordBreak: 'break-word',
            }}>
              {log.error}
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, fontSize: 10, color: '#475569', fontFamily: 'var(--font-mono)' }}>
            <span>{log.rows_in} in · {log.rows_out} out</span>
            <span>{formatTimeAgo(log.started_at, t)}</span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {hovered && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: '#475569', padding: 2, display: 'flex', alignItems: 'center' }}
              title={t('common.dismiss', 'Dismiss')}
            >
              <Trash2 size={12} />
            </button>
          )}
          {log.node_audits.length > 0 && (
            expanded
              ? <ChevronDown size={13} style={{ color: '#475569' }} />
              : <ChevronRight size={13} style={{ color: '#475569' }} />
          )}
        </div>
      </div>

      {expanded && log.node_audits.length > 0 && (
        <div style={{ padding: '0 16px 10px 38px', display: 'flex', flexDirection: 'column' }}>
          {log.node_audits.map((a, i) => <NodeRow key={i} audit={a} />)}
        </div>
      )}
    </div>
  );
};

// ─── Drawer ───────────────────────────────────────────────────────────────────

// ─── Actions tab ─────────────────────────────────────────────────────────────

const ActionRow: React.FC<{
  exec: ActionExecution;
  onConfirm: () => void;
  onReject: () => void;
}> = ({ exec, onConfirm, onReject }) => {
  const { t } = useTranslation();
  const title = (exec.inputs as Record<string, unknown>)?.title as string | undefined;
  const isAgent = exec.source === 'agent';
  const sev = (exec.inputs as Record<string, unknown>)?.severity as string | undefined;
  const sevColor = sev === 'critical' ? '#DC2626' : sev === 'high' ? '#D97706' : '#2563EB';

  return (
    <div style={{
      padding: '10px 16px',
      borderBottom: '1px solid #131C2E',
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        {isAgent ? <Bot size={12} style={{ color: '#3B82F6', flexShrink: 0, marginTop: 1 }} /> : <User size={12} style={{ color: '#64748B', flexShrink: 0, marginTop: 1 }} />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#E2E8F0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {title || exec.action_name}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
            <span style={{ fontSize: 10, fontFamily: 'monospace', color: '#64748B' }}>{exec.action_name}</span>
            {sev && (
              <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 4px', borderRadius: 2, color: sevColor, border: `1px solid ${sevColor}33`, textTransform: 'uppercase' }}>{sev}</span>
            )}
          </div>
        </div>
        <span style={{ fontSize: 10, color: '#475569', flexShrink: 0 }}>{formatTimeAgo(exec.created_at || '', t)}</span>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          onClick={onConfirm}
          style={{ flex: 1, height: 24, fontSize: 11, fontWeight: 600, cursor: 'pointer', borderRadius: 3, border: 'none', backgroundColor: '#065F46', color: '#6EE7B7' }}
        >
          {t('common.approve', 'Approve')}
        </button>
        <button
          onClick={onReject}
          style={{ flex: 1, height: 24, fontSize: 11, fontWeight: 600, cursor: 'pointer', borderRadius: 3, border: '1px solid #7F1D1D', backgroundColor: 'transparent', color: '#FCA5A5' }}
        >
          {t('common.reject', 'Reject')}
        </button>
      </div>
    </div>
  );
};

export const NotificationDrawer: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const {
    notifications, unreadCount: alertUnread,
    fetchNotifications, markRead: markAlertRead, markAllRead: markAllAlertRead,
    deleteNotification,
  } = useAlertStore();

  const {
    logs, unreadCount: logUnread,
    markRead: markLogRead, markAllRead: markAllLogRead,
    deleteLog,
  } = useRunLogStore();

  const { pending, pendingCount, confirm, reject } = useHumanActionsStore();
  const { pendingForMe, pendingCount: approvalCount, fetchPendingForMe, approve, reject: rejectApproval } = useApprovalStore();
  const { navigateTo, navigateToSettings } = useNavigationStore();

  // Open on the alerts when only they have news (a community answer, GSL-6).
  const [tab, setTab] = useState<'alerts' | 'logs' | 'actions' | 'approvals'>(
    () => (alertUnread > 0 && logUnread === 0 ? 'alerts' : 'logs'),
  );

  useEffect(() => {
    fetchNotifications();
    fetchPendingForMe();
  }, []);

  const tabStyle = (active: boolean): React.CSSProperties => ({
    flex: 1, textAlign: 'center', padding: '6px 0',
    fontSize: 12, fontWeight: 500, cursor: 'pointer', border: 'none',
    backgroundColor: 'transparent',
    color: active ? '#E2E8F0' : '#475569',
    borderBottom: active ? '2px solid #3B82F6' : '2px solid transparent',
    transition: 'color 80ms, border-color 80ms',
  });

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 200 }} onClick={onClose} />

      <div style={{
        position: 'fixed', top: 0, right: 0,
        width: 380, height: '100vh',
        backgroundColor: '#0F1824',
        borderLeft: '1px solid #1E2D42',
        display: 'flex', flexDirection: 'column',
        zIndex: 201,
        boxShadow: '-8px 0 32px rgba(0,0,0,0.4)',
      }}>
        {/* Header */}
        <div style={{
          height: 52, display: 'flex', alignItems: 'center', padding: '0 16px',
          borderBottom: '1px solid #1E2D42', gap: 10, flexShrink: 0,
        }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#E2E8F0', flex: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
            {tab === 'actions' ? <ShieldCheck size={14} /> : null}
            {tab === 'actions' ? t('nav.actions', 'Action Queue') : t('shell.notificationCenter', 'Notifications')}
          </span>
          {tab === 'alerts' && alertUnread > 0 && (
            <button
              onClick={() => markAllAlertRead()}
              style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#64748B', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 6px' }}
            >
              <CheckCheck size={13} /> {t('common.allRead', 'All read')}
            </button>
          )}
          {tab === 'logs' && logUnread > 0 && (
            <button
              onClick={() => markAllLogRead()}
              style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#64748B', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 6px' }}
            >
              <CheckCheck size={13} /> {t('common.allRead', 'All read')}
            </button>
          )}
          <button
            onClick={onClose}
            style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: '#475569', padding: 4, display: 'flex', alignItems: 'center' }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #1E2D42', flexShrink: 0 }}>
          <button style={tabStyle(tab === 'logs')} onClick={() => setTab('logs')}>
            {t('shell.pipelineRuns', 'Pipeline Runs')}
            {logUnread > 0 && (
              <span style={{ marginLeft: 5, fontSize: 10, backgroundColor: '#EF4444', color: '#FFF', borderRadius: 10, padding: '1px 5px' }}>
                {logUnread}
              </span>
            )}
          </button>
          <button style={tabStyle(tab === 'alerts')} onClick={() => setTab('alerts')}>
            {t('shell.alerts', 'Alerts')}
            {alertUnread > 0 && (
              <span style={{ marginLeft: 5, fontSize: 10, backgroundColor: '#EF4444', color: '#FFF', borderRadius: 10, padding: '1px 5px' }}>
                {alertUnread}
              </span>
            )}
          </button>
          <button style={tabStyle(tab === 'actions')} onClick={() => setTab('actions')}>
            {t('nav.actions', 'Actions')}
            {pendingCount > 0 && (
              <span style={{ marginLeft: 5, fontSize: 10, backgroundColor: '#D97706', color: '#FFF', borderRadius: 10, padding: '1px 5px' }}>
                {pendingCount}
              </span>
            )}
          </button>
          <button style={tabStyle(tab === 'approvals')} onClick={() => setTab('approvals')}>
            {t('shell.approvals', 'Approvals')}
            {approvalCount > 0 && (
              <span style={{ marginLeft: 5, fontSize: 10, backgroundColor: '#7C3AED', color: '#FFF', borderRadius: 10, padding: '1px 5px' }}>
                {approvalCount}
              </span>
            )}
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {tab === 'alerts' && (
            <>
              <button
                onClick={() => { navigateToSettings('alerts'); onClose(); }}
                style={{
                  width: '100%', textAlign: 'left', padding: '10px 14px', cursor: 'pointer',
                  background: 'none', border: 'none', borderBottom: '1px solid #131C2E',
                  color: '#A78BFA', fontSize: 12, fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <Settings size={13} /> {t('shell.manageAlertRules', 'Manage alert rules & channels →')}
              </button>
              {notifications.length === 0
                ? <EmptyState message={t('shell.noAlertsFired', 'No alerts have fired')} hint={t('shell.noAlertsHint', 'Create alerts from the bell icon on any dashboard widget, or manage them in Settings.')} />
                : notifications.map((n) => {
                  const question = communityQuestionOf(n);
                  if (question) {
                    return (
                      <CommunityRow
                        key={n.id}
                        notification={n}
                        onOpen={() => {
                          if (!n.read) void markAlertRead(n.id);
                          openAcademy({ view: 'community', question });
                          onClose();
                        }}
                        onDelete={() => deleteNotification(n.id)}
                      />
                    );
                  }
                  return (
                    <AlertRow
                      key={n.id}
                      notification={n}
                      onRead={() => !n.read && markAlertRead(n.id)}
                      onDelete={() => deleteNotification(n.id)}
                    />
                  );
                })}
            </>
          )}
          {tab === 'logs' && (
            logs.length === 0
              ? <EmptyState message={t('shell.noRunsYet', 'No pipeline runs yet')} hint={t('shell.noRunsHint', 'Run a pipeline to see execution logs here')} />
              : logs.map((log) => (
                <LogRow
                  key={log.id}
                  log={log}
                  onRead={() => !log.read && markLogRead(log.id)}
                  onDelete={() => deleteLog(log.id)}
                />
              ))
          )}
          {tab === 'actions' && (
            pending.length === 0
              ? <EmptyState message={t('humanActions.noPending', 'No pending actions')} hint={t('shell.noActionsHint', 'Agent-proposed actions awaiting your approval appear here')} />
              : pending.slice(0, 20).map((exec) => (
                <ActionRow
                  key={exec.id}
                  exec={exec}
                  onConfirm={() => confirm(exec.id, 'admin')}
                  onReject={() => reject(exec.id, 'admin', 'Rejected via notification center')}
                />
              ))
          )}
          {tab === 'approvals' && (
            pendingForMe.length === 0
              ? <EmptyState message={t('shell.noPendingApprovals', 'No pending approvals')} hint={t('shell.noApprovalsHint', 'Approval requests assigned to your role appear here')} />
              : pendingForMe.map((req) => (
                <ApprovalRow
                  key={req.id}
                  req={req}
                  onApprove={() => approve(req.id)}
                  onReject={(why) => rejectApproval(req.id, why || 'Rejected via notification center')}
                />
              ))
          )}
        </div>

        {/* Footer */}
        <div style={{
          height: 40, borderTop: '1px solid #1E2D42', padding: '0 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: 11, color: '#334155',
        }}>
          <span>
            {tab === 'alerts'
              ? t('shell.totalUnread', { total: notifications.length, unread: alertUnread, defaultValue: `${notifications.length} total · ${alertUnread} unread` })
              : tab === 'logs'
              ? t('shell.runsUnread', { count: logs.length, unread: logUnread, defaultValue: `${logs.length} runs · ${logUnread} unread` })
              : tab === 'approvals'
              ? t('shell.pendingForYou', { count: approvalCount, defaultValue: `${approvalCount} pending for you` })
              : t('shell.pendingCount', { count: pendingCount, defaultValue: `${pendingCount} pending` })}
          </span>
          {(tab === 'actions' || tab === 'approvals') && (
            <button
              onClick={() => { navigateTo(tab === 'approvals' ? 'audit' : 'human-actions'); onClose(); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 4, fontSize: 11,
                color: tab === 'approvals' ? '#A78BFA' : '#3B82F6',
                backgroundColor: 'transparent', border: 'none', cursor: 'pointer',
              }}
            >
              {t('common.viewAll', 'View all')} <ArrowUpRight size={11} />
            </button>
          )}
        </div>
      </div>
    </>
  );
};

const EmptyState: React.FC<{ message: string; hint: string }> = ({ message, hint }) => (
  <div style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', height: 200, gap: 8,
  }}>
    <span style={{ fontSize: 13, color: '#475569' }}>{message}</span>
    <span style={{ fontSize: 11, color: '#334155', textAlign: 'center', padding: '0 24px' }}>{hint}</span>
  </div>
);
